import React, { useState, useEffect } from "react";
import { Box, Typography, Select, MenuItem, FormControl } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import PageLoader from "../../components/Common/PageLoader";
import OverviewPanel from "./components/OverviewPanel";
import { useTheme } from "../../context/ThemeContext";
import { useClientFilter } from "../../hooks/useClientFilter";
import { apiClient } from "../../config/api";
import API_CONFIG from "../../config/api";

import LiveThreatFeed from "./components/LiveThreatFeed";
import IncidentCountByStatusSeverity from "./components/IncidentCountByStatusSeverity";
import ThreatDetectionTimeline from "./components/ThreatDetectionTimeline";
import TopRiskyEndpoints from "./components/TopRiskyEndpoints";
import MitreDetectionsCount from "./components/MitreDetectionsCount";
import CustomerRequests from "./components/CustomerRequests";
import "./dashboard.scss";

export default function Dashboard() {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("180d");
  const { selectedClientId } = useClientFilter();
  const [incidentsData, setIncidentsData] = useState([]);
  const [incidentsError, setIncidentsError] = useState(null);
  const [incidentsLoading, setIncidentsLoading] = useState(false);

  useEffect(() => {
    // Simulate data loading from backend
    const timer = setTimeout(() => {
      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // Fetch incidents once per timeRange/client filter for downstream widgets
  useEffect(() => {
    let mounted = true;
    const fetchIncidents = async () => {
      try {
        setIncidentsLoading(true);
        setIncidentsError(null);
        const params = {};
        if (timeRange) params.range = timeRange;
        if (timeRange.endsWith("d")) {
          const days = parseInt(timeRange.replace("d", ""), 10);
          if (!Number.isNaN(days)) params.days = days;
        }
        const { data } = await apiClient.get(API_CONFIG.ENDPOINTS.INCIDENTS, { params });
        let rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (selectedClientId) {
          const norm = (v) => (v ? v.toString().trim().toLowerCase() : "");
          const targetId = norm(selectedClientId);
          rows = rows.filter((r) =>
            [r.clientUUID, r.ClientUUID, r.clientId, r.ClientId].map(norm).includes(targetId)
          );
        }
        if (!mounted) return;
        setIncidentsData(rows);
      } catch (err) {
        if (!mounted) return;
        setIncidentsError(err.response?.data?.message || err.message || "Failed to load incidents");
        setIncidentsData([]);
      } finally {
        if (mounted) setIncidentsLoading(false);
      }
    };
    fetchIncidents();
    return () => {
      mounted = false;
    };
  }, [timeRange, selectedClientId]);

  if (loading) {
    return <PageLoader loading={loading} />;
  }

  return (
    <Box className="dashboard">
      <Box className="dashboard__content">
        {/* Dashboard Title Section */}
        {/* Dashboard Title Section */}
        <Box className="dashboard__header">
          <Box>
            <Typography variant="h4" className="dashboard__page-title">
              Dashboard
            </Typography>
            <Typography variant="body1" className="dashboard__page-subtitle">
              Real-time threat detection
            </Typography>
          </Box>
          <FormControl size="small" className="dashboard__time-select">
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              displayEmpty
              IconComponent={ArrowDropDown}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: theme === 'light' ? '#ffffff' : '#091619',
                    borderRadius: '2px',
                    '& .MuiMenuItem-root': {
                      color: theme === 'light' ? '#0f172a' : 'white',
                      '&.Mui-selected': {
                        bgcolor: theme === 'light' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(11, 98, 88, 0.7)',
                      },
                      '&.Mui-selected:hover': {
                        bgcolor: theme === 'light' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(11, 98, 88, 0.7)',
                      },
                      '&:hover': {
                        bgcolor: theme === 'light' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(11, 98, 88, 0.7)',
                      },
                    },
                  },
                },
              }}
              sx={{
                color: theme === 'light' ? '#0f172a' : 'white',
                fontSize: "13px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme === 'light' ? 'rgba(0, 0, 0, 0.2)' : "rgba(255, 255, 255, 0.2)",
                },
                "&:hover": {
                  color: theme === 'light' ? '#000000' : 'white',
                  backgroundColor: theme === 'light' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(11, 98, 88, 0.3)',
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme === 'light' ? 'rgba(0, 0, 0, 0.3)' : "rgba(255, 255, 255, 0.3)",
                },
                "& .MuiSvgIcon-root": {
                  color: theme === 'light' ? '#0f172a' : "white",
                },
                "&:hover .MuiSvgIcon-root": {
                  color: theme === 'light' ? '#000000' : "white",
                },
              }}
            >
              <MenuItem className="menuItem" value="24h">24 Hours</MenuItem>
              <MenuItem className="menuItem" value="7d">7 Days</MenuItem>
              <MenuItem className="menuItem" value="30d">30 Days</MenuItem>
              <MenuItem className="menuItem" value="90d">90 Days</MenuItem>
              <MenuItem className="menuItem" value="180d">180 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* First Row: Overview (left) and Live Threat Feed (right) */}
        <Box className="dashboard__row dashboard__row--first">
          <Box className="dashboard__left-col">
            <Box className="dashboard__panel-header">
              <Box>
                <Typography variant="h5" className="dashboard__widget-title">
                  Overview
                </Typography>
                <Typography
                  variant="body2"
                  className="dashboard__widget-subtitle"
                >
                  Security posture and threat intelligence metrics
                </Typography>
              </Box>
            </Box>
            <OverviewPanel range={timeRange} clientId={selectedClientId || null} />
          </Box>
          <Box className="dashboard__feed-col">
            <LiveThreatFeed />
          </Box>
        </Box>

        {/* Second Row: Incident Chart (left) + Timeline (center) + Risky Endpoints (right) */}
        <Box className="dashboard__row dashboard__row--second">
          <Box className="dashboard__incident-chart-col">
            <IncidentCountByStatusSeverity
              range={timeRange}
              clientId={selectedClientId || null}
              incidents={incidentsData}
              loadingExternal={incidentsLoading}
              errorExternal={incidentsError}
            />
          </Box>
          <Box className="dashboard__timeline-col">
            <ThreatDetectionTimeline
              range={timeRange}
              clientId={selectedClientId || null}
              incidents={incidentsData}
              loadingExternal={incidentsLoading}
              errorExternal={incidentsError}
            />
          </Box>
          <Box className="dashboard__risky-col">
            <TopRiskyEndpoints range={timeRange} clientId={selectedClientId || null} />
          </Box>
        </Box>

        {/* Third Row: MITRE Detections + Customer Requests */}
        <Box className="dashboard__row dashboard__row--third">
          <MitreDetectionsCount
            range={timeRange}
            clientId={selectedClientId || null}
            incidents={incidentsData}
            loadingExternal={incidentsLoading}
            errorExternal={incidentsError}
          />
          <CustomerRequests />
        </Box>
      </Box>
    </Box>
  );
}
